import { QueryKey, useQuery } from "@tanstack/react-query";
import { config } from "@/config";
import { appFetch } from "@/utils/fetchInterceptor";

interface UseFetchResult<T, F> {
  url: string;
  options?: RequestInit & { skip?: boolean };
  onFetched?: (data: T | null) => void;
  formatter?: (data: T) => F;
  baseUrl?: string;
  isCaching?: boolean;
  queryKey?: QueryKey;
}

export function useFetch<T = unknown, F = unknown>({
  url,
  options,
  onFetched,
  formatter,
  baseUrl,
  isCaching = false,
  queryKey = ["fetch"],
}: UseFetchResult<T, F>) {
  const query = useQuery({
    queryKey: [...queryKey, url],
    enabled: !options?.skip,
    staleTime: isCaching ? Infinity : 0,
    refetchOnWindowFocus: !isCaching,
    queryFn: async () => {
      const finalBase = baseUrl || config.apiDomain;
      const fullUrl = url.startsWith("http") ? url : `${finalBase}${url}`;

      const res = await appFetch(fullUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(options?.headers || {}),
        },
        ...options,
      });

      if (!res.ok) throw new Error(await res.text());

      const json = (await res.json()) as T;
      onFetched?.(json);

      return formatter ? formatter(json) : json;
    },
  });

  return {
    data: (query.data as T) ?? null,
    formattedData: formatter ? (query.data as F) : undefined,
    loading: query.isLoading,
    fetching: query.isFetching,
    error: query.error instanceof Error ? query.error.message : null,
    refetch: query.refetch,

    source: isCaching && !query.isFetchedAfterMount ? "cache" : "api",
  };
}

export default useFetch;
